import { NgClass } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import {
	AbstractControl,
	ControlContainer,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { isDate } from 'src/lib/utilities/compare';
import { SingleDatePickerComponent } from '../single-date-picker/single-date-picker.component';

export interface PeriodModel {
	from: FormControl<Date>;
	to: FormControl<Date>;
}

@Component({
	selector: 'ae-range-date-picker',
	imports: [NgClass, ReactiveFormsModule, SingleDatePickerComponent],
	templateUrl: './range-date-picker.component.html',
	styleUrl: './range-date-picker.component.scss',
})
export class RangeDatePickerComponent implements OnInit {
	readonly formGroup = input<FormGroup<PeriodModel>>();
	readonly groupName = input<string>();
	readonly labelForId = input.required<string>();
	readonly label = input<string>(null);
	readonly labelInvalid = input<boolean>(false);

	public group = signal<FormGroup<PeriodModel | null>>(null);

	private controlContainer = inject(ControlContainer, { optional: true });
	private isRequired = false;

	ngOnInit(): void {
		let g: FormGroup<PeriodModel>;
		if (this.formGroup()) {
			g = this.formGroup();
		} else if (this.groupName() && this.controlContainer) {
			g = this.controlContainer.control.get(
				this.groupName(),
			) as FormGroup<PeriodModel>;
		}

		if (g) {
			this.isRequired = g.hasValidator(Validators.required);
			g.addValidators(this.periodValidator);
			g.updateValueAndValidity();
			this.group.set(g);
		}

		if (!g) {
			throw new Error(
				'ae-range-date-picker requires either [formGroup] or [groupName]',
			);
		}
	}

	private periodValidator = (
		control: AbstractControl,
	): ValidationErrors | null => {
		const value = control.value;
		const hasFrom = isDate(value.from);
		const hasTo = isDate(value.to);

		if (this.isRequired) {
			if (!hasFrom || !hasTo) {
				return { required: true };
			}
		} else {
			if (hasFrom && !hasTo) {
				return { toRequired: true };
			}

			if (hasTo && !hasFrom) {
				return { fromRequired: true };
			}
		}

		if (hasFrom && hasTo && value.to < value.from) {
			return { invalidPeriod: true };
		}

		return null;
	};

	hasVisibleError(): boolean {
		if (this.labelInvalid()) return true;

		const g = this.group();
		if (!g?.errors) return false;

		const from = g.get('from');
		const to = g.get('to');

		if (g.errors['required']) {
			return from.touched && from.dirty && to.touched && to.dirty;
		}

		if (g.errors['fromRequired'] || g.errors['toRequired']) {
			return from.touched && from.dirty && to.touched && to.dirty;
		}

		return true;
	}
}
